import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Plus, ChevronRight } from 'lucide-react';
import Card from '../../components/common/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import Button from '../../components/common/Button';
import Toast from '../../components/common/Toast';
import { getTeams } from '../../services/teamService';
import './Teams.css';

/**
 * TeamList Page (Task 12.1)
 *
 * Admin-only page listing all teams (active and disabled).
 * Requirements: R1.1, R5.2
 */
export default function TeamList() {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'active' | 'disabled'
  const [toast, setToast] = useState(null);

  const fetchTeams = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await getTeams(params);
      setTeams(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);

  const filteredTeams = teams;

  return (
    <div className="teams-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title">
              <Shield size={32} />
              Teams
            </h1>
            <p className="page-subtitle">Manage tenant teams and their ingest configurations</p>
          </div>
          <Button variant="primary" onClick={() => navigate('/admin/teams/create')} icon={<Plus size={20} />}>
            Create Team
          </Button>
        </div>
      </div>

      <Card className="teams-card">
        <div className="teams-controls">
          <div className="teams-filter">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
              onClick={() => setFilter('active')}
            >
              Active
            </button>
            <button
              className={`filter-btn ${filter === 'disabled' ? 'active' : ''}`}
              onClick={() => setFilter('disabled')}
            >
              Disabled
            </button>
          </div>
        </div>

        {loading && <LoadingSpinner />}
        {error && <ErrorMessage message={error} onRetry={fetchTeams} />}

        {!loading && !error && (
          <div className="teams-list">
            {filteredTeams.length === 0 ? (
              <div className="empty-state">
                <Shield size={48} className="empty-icon" />
                <p>No teams found</p>
              </div>
            ) : (
              filteredTeams.map(team => (
                <div
                  key={team.team_id}
                  className={`team-row ${team.status === 'disabled' ? 'team-row-disabled' : ''}`}
                  onClick={() => navigate(`/admin/teams/${team.team_id}`)}
                >
                  <div className="team-row-info">
                    <div className="team-row-name">
                      <span className="team-id-badge">{team.team_id}</span>
                      <span className="team-display-name">{team.name}</span>
                      {team.is_default && <span className="default-badge">Default</span>}
                    </div>
                    {team.description && (
                      <p className="team-row-desc">{team.description}</p>
                    )}
                    <div className="team-row-meta">
                      <span className="team-meta-item">{team.contact_email}</span>
                      <span className="team-meta-item">Account: {team.source_account_id}</span>
                    </div>
                  </div>
                  <div className="team-row-right">
                    <span className={`status-pill status-${team.status}`}>
                      {team.status}
                    </span>
                    <ChevronRight size={20} className="team-row-chevron" />
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </Card>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={5000} />}
    </div>
  );
}
